import { useState, useEffect } from "react";
import { Filter, X, ChevronDown, Sliders, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ZeltKategorie, ZeltTag } from "@/data/zelte";
import { getMarken } from "@/data/zelte";

export interface FilterState {
  kategorie: ZeltKategorie | null;
  personen: number | null;
  saison: 3 | 4 | null;
  minPreis: number | null;
  maxPreis: number | null;
  tags: ZeltTag[];
  marke: string | null;
  maxGewicht: number | null;
  minWassersaeule: number | null;
  sortBy: "preis" | "bewertung" | "name" | "personen" | "gewicht";
  order: "asc" | "desc";
}

interface FilterSidebarProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  className?: string;
  mobile?: boolean;
}

const kategorieOptions: { value: ZeltKategorie; label: string }[] = [
  { value: "kuppelzelt", label: "Kuppelzelte" },
  { value: "tunnelzelt", label: "Tunnelzelte" },
  { value: "familienzelt", label: "Familienzelte" },
  { value: "trekkingzelt", label: "Trekkingzelte" },
  { value: "geodaet", label: "Geodät-Zelte" },
  { value: "tipi", label: "Tipi-Zelte" },
  { value: "wurfzelt", label: "Wurfzelte" },
  { value: "dachzelt", label: "Dachzelte" },
];

const personenOptions = [1, 2, 3, 4, 5, 6, 8];

const preisRanges = [
  { min: null, max: 100, label: "Bis 100€" },
  { min: 100, max: 300, label: "100€ - 300€" },
  { min: 300, max: 500, label: "300€ - 500€" },
  { min: 500, max: null, label: "Ab 500€" },
];

const tagOptions: { value: ZeltTag; label: string; color: string }[] = [
  { value: "bestseller", label: "Bestseller", color: "from-orange-500/20 to-amber-500/20" },
  { value: "empfohlen", label: "Empfohlen", color: "from-emerald-500/20 to-green-500/20" },
  { value: "neu", label: "Neu", color: "from-blue-500/20 to-indigo-500/20" },
  { value: "sale", label: "Im Angebot", color: "from-red-500/20 to-rose-500/20" },
  { value: "leicht", label: "Ultraleicht", color: "from-cyan-500/20 to-sky-500/20" },
  { value: "premium", label: "Premium", color: "from-purple-500/20 to-violet-500/20" },
];


function FilterSection({
  title,
  children,
  defaultOpen = true,
  icon
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  icon?: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-border/50 pb-5 mb-5 last:border-0 last:pb-0 last:mb-0">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "group flex items-center justify-between w-full text-left font-semibold mb-4",
          "py-1 -my-1 rounded-lg cursor-pointer select-none",
          "transition-all duration-200 hover:text-primary"
        )}
      >
        <span className="flex items-center gap-2">
          {icon}
          {title}
        </span>
        <span className={cn(
          "transition-transform duration-300 ease-out",
          open && "rotate-180"
        )}>
          <ChevronDown className="h-4 w-4" />
        </span>
      </button>
      <div className={cn(
        "transition-all duration-300 ease-out overflow-hidden",
        open ? "max-h-[640px] opacity-100 overflow-y-auto" : "max-h-0 opacity-0 overflow-hidden"
      )}>
        {children}
      </div>
    </div>
  );
}

export function FilterSidebar({ filters, onChange, className, mobile = false }: FilterSidebarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close on Escape key
  useEffect(() => {
    if (!mobileOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [mobileOpen]);

  // Prevent body scroll when mobile filter is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    onChange({ ...filters, [key]: value });
  };

  const toggleTag = (tag: ZeltTag) => {
    const newTags = filters.tags.includes(tag)
      ? filters.tags.filter(t => t !== tag)
      : [...filters.tags, tag];
    updateFilter("tags", newTags);
  };

  const resetFilters = () => {
    onChange({
      kategorie: null,
      personen: null,
      saison: null,
      minPreis: null,
      maxPreis: null,
      tags: [],
      marke: null,
      maxGewicht: null,
      minWassersaeule: null,
      sortBy: "bewertung",
      order: "desc",
    });
  };

  const activeFilterCount = [
    filters.kategorie,
    filters.personen,
    filters.saison,
    filters.minPreis || filters.maxPreis,
    filters.tags.length > 0,
    filters.marke,
    filters.maxGewicht,
    filters.minWassersaeule,
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0;

  const content = (
    <div className={cn("space-y-1", className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Sliders className="w-4 h-4 text-primary" />
          </div>
          <span className="font-bold">Filter</span>
          {activeFilterCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-semibold bg-primary text-primary-foreground rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            className="h-8 text-xs text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="w-3 h-3 mr-1" />
            Zurücksetzen
          </Button>
        )}
      </div>

      {/* Kategorie */}
      <FilterSection title="Kategorie">
        <div className="space-y-1.5">
          {kategorieOptions.map(option => (
            <button
              key={option.value}
              onClick={() => updateFilter("kategorie", filters.kategorie === option.value ? null : option.value)}
              className={cn(
                "flex items-center w-full text-left text-sm py-2.5 px-3 rounded-xl cursor-pointer select-none",
                "transition-all duration-200 ease-out",
                filters.kategorie === option.value
                  ? "bg-primary text-primary-foreground font-medium shadow-sm shadow-primary/20"
                  : "hover:bg-muted/60 active:scale-[0.98]"
              )}
            >
              <span className={cn(
                "w-1.5 h-1.5 rounded-full mr-2.5 transition-all duration-200",
                filters.kategorie === option.value ? "bg-primary-foreground scale-100" : "bg-muted-foreground/30 scale-75"
              )} />
              {option.label}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Personen */}
      <FilterSection title="Personenanzahl">
        <div className="flex flex-wrap gap-2">
          {personenOptions.map(num => (
            <button
              key={num}
              onClick={() => updateFilter("personen", filters.personen === num ? null : num)}
              className={cn(
                "w-11 h-11 rounded-xl text-sm font-semibold cursor-pointer select-none",
                "transition-all duration-200 ease-out",
                "border-2",
                filters.personen === num
                  ? "bg-primary text-primary-foreground border-primary shadow-sm shadow-primary/20 scale-105"
                  : "bg-muted/40 border-transparent hover:border-border hover:bg-muted/60 active:scale-95"
              )}
            >
              {num}+
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Saison */}
      <FilterSection title="Saison">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => updateFilter("saison", filters.saison === 3 ? null : 3)}
            className={cn(
              "py-3 px-4 rounded-xl text-sm font-semibold cursor-pointer select-none",
              "transition-all duration-200 ease-out",
              "border-2",
              filters.saison === 3
                ? "bg-primary text-primary-foreground border-primary shadow-sm shadow-primary/20"
                : "bg-muted/40 border-transparent hover:border-border hover:bg-muted/60 active:scale-[0.98]"
            )}
          >
            3-Saison
          </button>
          <button
            onClick={() => updateFilter("saison", filters.saison === 4 ? null : 4)}
            className={cn(
              "py-3 px-4 rounded-xl text-sm font-semibold cursor-pointer select-none",
              "transition-all duration-200 ease-out",
              "border-2",
              filters.saison === 4
                ? "bg-primary text-primary-foreground border-primary shadow-sm shadow-primary/20"
                : "bg-muted/40 border-transparent hover:border-border hover:bg-muted/60 active:scale-[0.98]"
            )}
          >
            4-Saison
          </button>
        </div>
      </FilterSection>

      {/* Preis */}
      <FilterSection title="Preis">
        <div className="space-y-1.5">
          {preisRanges.map((range, i) => {
            const isActive = filters.minPreis === range.min && filters.maxPreis === range.max;
            return (
              <button
                key={i}
                onClick={() => {
                  if (isActive) {
                    onChange({ ...filters, minPreis: null, maxPreis: null });
                  } else {
                    onChange({ ...filters, minPreis: range.min, maxPreis: range.max });
                  }
                }}
                className={cn(
                  "flex items-center w-full text-left text-sm py-2.5 px-3 rounded-xl cursor-pointer select-none",
                  "transition-all duration-200 ease-out",
                  isActive
                    ? "bg-primary text-primary-foreground font-medium shadow-sm shadow-primary/20"
                    : "hover:bg-muted/60 active:scale-[0.98]"
                )}
              >
                <span className={cn(
                  "w-1.5 h-1.5 rounded-full mr-2.5 transition-all duration-200",
                  isActive ? "bg-primary-foreground scale-100" : "bg-muted-foreground/30 scale-75"
                )} />
                {range.label}
              </button>
            );
          })}
        </div>
      </FilterSection>

      {/* Gewicht */}
      <FilterSection title="Gewicht">
        <div className="space-y-1.5">
          {[
            { max: 2, label: "Bis 2 kg" },
            { max: 3.5, label: "Bis 3,5 kg" },
            { max: 8, label: "Bis 8 kg" },
          ].map(range => {
            const isActive = filters.maxGewicht === range.max;
            return (
              <button
                key={range.max}
                onClick={() => updateFilter("maxGewicht", isActive ? null : range.max)}
                className={cn(
                  "flex items-center w-full text-left text-sm py-2.5 px-3 rounded-xl cursor-pointer select-none",
                  "transition-all duration-200 ease-out",
                  isActive
                    ? "bg-primary text-primary-foreground font-medium shadow-sm shadow-primary/20"
                    : "hover:bg-muted/60 active:scale-[0.98]"
                )}
              >
                {range.label}
              </button>
            );
          })}
        </div>
      </FilterSection>

      {/* Wassersäule */}
      <FilterSection title="Wassersäule">
        <div className="space-y-1.5">
          {[
            { min: 3000, label: "ab 3.000 mm" },
            { min: 5000, label: "ab 5.000 mm" },
            { min: 8000, label: "ab 8.000 mm" },
          ].map(range => {
            const isActive = filters.minWassersaeule === range.min;
            return (
              <button
                key={range.min}
                onClick={() => updateFilter("minWassersaeule", isActive ? null : range.min)}
                className={cn(
                  "flex items-center w-full text-left text-sm py-2.5 px-3 rounded-xl cursor-pointer select-none",
                  "transition-all duration-200 ease-out",
                  isActive
                    ? "bg-primary text-primary-foreground font-medium shadow-sm shadow-primary/20"
                    : "hover:bg-muted/60 active:scale-[0.98]"
                )}
              >
                {range.label}
              </button>
            );
          })}
        </div>
      </FilterSection>

      {/* Marke */}
      <FilterSection title="Marke" defaultOpen={false}>
        <div className="flex flex-wrap gap-2">
          {getMarken().map(marke => (
            <button
              key={marke}
              onClick={() => updateFilter("marke", filters.marke === marke ? null : marke)}
              className={cn(
                "px-3 py-1.5 rounded-xl text-xs font-semibold cursor-pointer select-none",
                "transition-all duration-200 ease-out border-2",
                filters.marke === marke
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-muted/40 border-transparent hover:border-border"
              )}
            >
              {marke}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Tags */}
      <FilterSection title="Eigenschaften">
        <div className="flex flex-wrap gap-2">
          {tagOptions.map(option => (
            <button
              key={option.value}
              onClick={() => toggleTag(option.value)}
              className={cn(
                "px-3.5 py-2 rounded-xl text-xs font-semibold cursor-pointer select-none",
                "transition-all duration-200 ease-out",
                "border-2",
                filters.tags.includes(option.value)
                  ? "bg-primary text-primary-foreground border-primary shadow-sm shadow-primary/20 scale-105"
                  : `bg-gradient-to-r ${option.color} border-transparent hover:border-border active:scale-95`
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </FilterSection>
    </div>
  );

  if (mobile) {
    return (
      <>
        <Button
          variant="outline"
          onClick={() => setMobileOpen(true)}
          className="lg:hidden h-10 px-4 rounded-xl gap-2"
        >
          <Filter className="h-4 w-4" />
          Filter
          {hasActiveFilters && (
            <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </Button>

        {/* Mobile Overlay */}
        <div className={cn(
          "fixed inset-0 z-50 lg:hidden transition-opacity duration-300",
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}>
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className={cn(
            "absolute right-0 top-0 bottom-0 w-[320px] max-w-[calc(100%-48px)] bg-background p-6 overflow-y-auto",
            "transition-transform duration-500 ease-out",
            mobileOpen ? "translate-x-0" : "translate-x-full"
          )}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Filter & Sortierung</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(false)}
                className="h-10 w-10 rounded-xl"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            {content}
          </div>
        </div>
      </>
    );
  }

  return content;
}
